/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IBlock } from '@datatype/type';
import BoxBlock from '@component/common/block/box-block';
import IconBlock from '@component/common/block/icon-block';
import { useClicker } from '@hook/common/useEvent';
import TextBlock from '@component/common/block/text-block';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  sources: string[];
  timing: {
    begin?: number;
    gap?: number;
    end?: number;
  };
  replay: boolean;
  design: Pick<IBlock['design'], 'box' | 'text'>;
}

/**
 * @deprecated Use TextAudioButton design system component instead.
 */
const TextAudiosBlock: React.FC<IProps> = ({ design, sources, timing = {}, replay }) => {
  const sourcesKey = sources?.join('|') ?? '';
  const stableSources = useMemo(() => sources, [sourcesKey]);

  const audioRefs = useRef<HTMLAudioElement[]>([]);
  const [currentAudioIdx, setCurrentAudioIdx] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isInitial, setIsInitial] = useState<boolean>(true);
  const [isReplayable, setIsReplayable] = useState<boolean>(false);
  const playSessionRef = useRef<number>(0);

  const sleep = (time: number) => new Promise((resolve) => setTimeout(resolve, time));

  const stopAllAudio = () => {
    audioRefs.current.forEach((a) => {
      if (a) {
        a.pause();
        a.currentTime = 0;
      }
    });
  };

  // sources 실제 변경 시 재생 중인 오디오 정지
  useEffect(() => {
    return () => {
      playSessionRef.current += 1;
      stopAllAudio();
      setIsPlaying(false);
      setCurrentAudioIdx(0);
      setIsInitial(true);
    };
  }, [stableSources]);

  // unmount 시 정리
  useEffect(() => {
    return () => {
      playSessionRef.current += 1;
      stopAllAudio();
    };
  }, []);

  // 다음 audio 재생
  useEffect(() => {
    const sessionId = playSessionRef.current;

    if (!isPlaying) return;

    (async () => {
      if (currentAudioIdx === 0 && isInitial) {
        await sleep(timing?.begin || 0);
        if (sessionId !== playSessionRef.current) return;
        setIsInitial(false);
        return;
      }

      if (currentAudioIdx > 0) {
        await sleep(timing?.gap || 0);
      }

      if (sessionId !== playSessionRef.current) return;

      const audio = audioRefs.current[currentAudioIdx];
      if (!audio) return;

      try {
        await audio.play();
      } catch {
        // play()가 pause()에 의해 중단된 경우 무시
      }
    })();
  }, [currentAudioIdx, isPlaying, isInitial, timing]);

  // audio 재생이 끝났을때 로직
  const handlePlayCompleted = async () => {
    const sessionId = playSessionRef.current;

    if (currentAudioIdx < sources.length - 1) {
      setCurrentAudioIdx((prev) => prev + 1);
    } else {
      await sleep(timing?.end || 0);
      if (sessionId !== playSessionRef.current) return;
      setIsPlaying(false);
      setCurrentAudioIdx(0);
      setIsInitial(true);
    }
  };

  // audio play 버튼 클릭했을때 로직
  const handlePlayAndPauseClick = useClicker(
    async () => {
      if (!isPlaying) {
        if (isInitial) {
          setIsReplayable(true);
        }
        setIsPlaying(true);
      } else {
        playSessionRef.current += 1;
        stopAllAudio();
        setIsPlaying(false);
        setCurrentAudioIdx(0);
        setIsInitial(true);
      }
    },
    500,
    'throttle',
  );

  // audio replay 버튼 클릭했을때 로직
  const handleReplayClick = useClicker(
    async () => {
      playSessionRef.current += 1;
      stopAllAudio();
      setCurrentAudioIdx(0);
      setIsInitial(true);
      setIsPlaying(true);
    },
    500,
    'throttle',
  );

  return (
    <BoxBlock design={{ box: design.box }} onClick={handlePlayAndPauseClick}>
      <TextBlock
        text={isPlaying ? '일시정지' : '듣기'}
        design={{ text: design.text }}
        // click={{
        //   handler: handlePlayAndPauseClick,
        // }}
      />
      {replay && isReplayable && (
        <IconBlock
          icon={'replay'}
          design={{
            icon: { size: 'medium', kind: 'outlined', color: color.BROWN_1 },
            box: { event: { cursor: 'pointer' } },
          }}
          click={{
            handler: handleReplayClick,
          }}
        />
      )}
      {sources.map((src, idx) => (
        <audio
          key={idx}
          ref={(element) => {
            if (element) audioRefs.current[idx] = element;
          }}
          src={src}
          onEnded={handlePlayCompleted}
          controls={false}
        />
      ))}
    </BoxBlock>
  );
};

export default TextAudiosBlock;
